<!--@format-->
<template>
    <div class="journey-condition-step-widget">
        <div class="widget-contents-container">
            <div class="widget-header dark-blue-gradient-bg">
                <h3>Condition</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="condition-name-container">
                        <app-input v-model="step.name" :label="'Step Name'" />
                    </div>
                    <div v-show="step.rules.length > 1" class="condition-container">
                        <div>Condition Matches</div>
                        <app-input-radio v-model="step.matchSelection" :options="matchOptions" />
                    </div>
                    <div v-for="rule in step.rules" :key="rule.id">
                        <journey-condition-rule :rule="rule" @remove="removeRule(rule)" />

                        <div v-show="shouldShowBoolean(rule)" class="condition-operator-container">
                            <div class="condition-operator">
                                {{ condition() }}
                            </div>
                        </div>

                        <div class="line-container"><div class="line"></div></div>
                    </div>
                    <div class="add-step-box">
                        <app-button class="add-btn" @click="addRule">
                            <plus-sign-svg class="plus-sign" />
                        </app-button>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isLoading" :loading="isLoading" @click="save"
                        >Save</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const PlusSignSvg = () => import('@/components/svg/plus-icon-svg');
const JourneyConditionRule = () => import('@/components/journey/journey-condition-rule');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'journey-condition-step-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-input-radio': AppInputRadio,
        'widget-close-svg': WidgetCloseSvg,
        'plus-sign-svg': PlusSignSvg,
        'journey-condition-rule': JourneyConditionRule,
        'loading-screen-svg': LoadingScreenSvg,
    },

    provide() {
        return {
            step: this.step,
        };
    },

    props: {
        options: {
            type: Object,
            default: null,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            matchOptions: [
                {
                    name: 'All of the rules',
                    value: 'all',
                },
                {
                    name: 'Any of the rules',
                    value: 'any',
                },
            ],

            step: {
                id: null,
                name: null,
                journeyId: null,
                matchSelection: null,
                rules: [],
            },
        };
    },

    created() {
        if (this.widget.options.stepId) {
            this.step.id = this.widget.options.stepId;
            this.getStep();
        } else {
            this.step.journeyId = this.widget.options.journeyId;
            this.addRule();
            this.step.matchSelection = this.matchOptions[0];
            this.isLoading = false;
        }
    },

    methods: {
        removeRule(rule) {
            this.step.rules.splice(this.step.rules.indexOf(rule), 1);
        },

        save() {
            this.isLoading = true;

            this.formatLinks();

            if (this.step.id) {
                this.patchStep()
                    .then(this.updateRules)
                    .then(() => {
                        this.$helpers.showSuccessNotification('Step successfully saved.');
                        EventBus.$emit(`journey-step:update`, this.step);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your step.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                this.postStep()
                    .then(this.updateRules)
                    .then(() => {
                        this.$helpers.showSuccessNotification('Step successfully saved.');
                        EventBus.$emit(`journey-step:update`, this.step);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your step.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },

        patchStep() {
            var changes = [
                {
                    name: 'Name',
                    value: this.step.name,
                },
                {
                    name: 'IsConditionUnion',
                    value: this.step.matchSelection === this.matchOptions[1],
                },
            ];

            return this.$api.patch(`/api/JourneySteps/${this.step.id}`, changes);
        },

        postStep() {
            return new Promise((resolve, reject) => {
                var request = {
                    journeyId: this.step.journeyId,
                    journeyTemplateId: this.step.journeyTemplateId,
                    name: this.step.name,
                    isConditionUnion: this.step.matchSelection === this.matchOptions[1],
                };

                this.$api
                    .post(`/api/JourneySteps`, request)
                    .then((response) => {
                        this.step.id = response.data.Id;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        formatLinks() {
            this.step.rules.forEach((rule) => {
                if (
                    rule.condition === 'had a specific link clicked' &&
                    !rule.value.includes('http')
                ) {
                    rule.value = 'http://' + rule.value;
                }
            });
        },

        updateRules() {
            var completeRules = this.step.rules.filter(
                (rule) => rule.type && rule.target && rule.condition,
            );
            var request = completeRules.map(this.mapRuleRequest);

            return this.$api.put(`/api/JourneySteps/${this.step.id}/Rules`, request);
        },

        mapRuleRequest(rule) {
            var value = null;
            if (rule.value instanceof Date) {
                value = this.$helpers.getMMDDYYYY(rule.value);
            } else {
                value = rule.value;
            }

            var target = null;
            if (rule.target instanceof Object) {
                target = rule.target.id;
            } else {
                target = rule.target;
            }

            return {
                journeyStepId: this.step.id,
                journeyConditionRuleTypeId: rule.type.id,
                target: target,
                condition: rule.condition,
                value: value,
            };
        },

        hydrateEmailStepTarget(target) {
            this.$api
                .get(`/service/JourneySteps?$select=Name&$filter=Id eq ${target.id}`)
                .then((response) => {
                    target.name = response.data.value[0].Name || 'No name defined';
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to fetch email info.', { error });
                });
        },

        hydrateCampaignTarget(target) {
            this.$api
                .get(`/service/Campaigns?$select=Name&$filter=Id eq ${target.id}`)
                .then((response) => {
                    target.name = response.data.value[0].Name;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to fetch campaign info.', { error });
                });
        },

        getStep() {
            this.$api
                .get(
                    `/service/JourneySteps?$filter=Id eq ${this.step.id}&$expand=JourneyConditionRules($expand=JourneyConditionRuleType)`,
                )
                .then((response) => {
                    var responseValue = response.data.value[0];

                    this.step.journeyId = responseValue.JourneyId;
                    this.step.journeyTemplateId = responseValue.JourneyTemplateId;
                    this.step.name = responseValue.Name;
                    this.step.matchSelection = responseValue.IsConditionUnion
                        ? this.matchOptions[1]
                        : this.matchOptions[0];
                    this.step.rules = responseValue.JourneyConditionRules.map(this.mapRuleResponse);

                    if (this.step.rules.length === 0) {
                        this.addRule();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your step.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        mapRuleResponse(rule) {
            var value = rule.Value;
            if (rule.Target === 'Date Subscribed') {
                value = new Date(rule.Value);
            }

            var target = rule.Target;
            if (!isNaN(target)) {
                target = {
                    id: parseInt(target),
                    name: null,
                };

                if (rule.JourneyConditionRuleType.Name === 'Journey Activity') {
                    this.hydrateEmailStepTarget(target);
                } else if (rule.JourneyConditionRuleType.Name === 'Campaign Activity') {
                    this.hydrateCampaignTarget(target);
                }
            }

            return {
                id: rule.Id,
                type: {
                    id: rule.JourneyConditionRuleTypeId,
                    name: rule.JourneyConditionRuleType.Name,
                },
                target: target,
                condition: rule.Condition,
                value: value,
            };
        },

        condition() {
            if (this.step.matchSelection === this.matchOptions[1]) {
                return 'OR';
            } else {
                return 'AND';
            }
        },

        shouldShowBoolean(rule) {
            return this.step.rules.length > 1 && !this.isLastRule(rule);
        },

        isLastRule(rule) {
            return this.step.rules.indexOf(rule) === this.step.rules.length - 1;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        addRule() {
            this.step.rules.push({
                id: this.$helpers.createGuid(),
                type: null,
                target: null,
                condition: null,
                value: null,
            });
        },
    },
};
</script>

<style>
.journey-condition-step-widget .condition-name-container {
    margin-bottom: 24px;
}

.journey-condition-step-widget .condition-container .options {
    display: flex;
    margin-top: 13px;
}

.journey-condition-step-widget .condition-container .option:last-of-type {
    margin-left: 100px;
}

.journey-condition-step-widget .app-multiselect {
    margin-bottom: 12px;
}

.journey-condition-step-widget .value-input {
    margin-bottom: 4px;
}

.journey-condition-step-widget .condition-operator-container {
    display: flex;
    justify-content: center;
}

.journey-condition-step-widget .condition-operator-container .condition-operator {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 129px;
    height: 51px;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.journey-condition-step-widget .add-step-box .add-btn {
    width: 100%;
    height: 39px;
    background-color: #18aeff;
    border-radius: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
}

.journey-condition-step-widget .add-step-box .add-btn .plus-sign {
    height: 21px;
    width: 21px;
}

.journey-condition-step-widget .add-step-box .add-btn .plus-sign .default {
    fill: #ffffff;
}

.journey-condition-step-widget .btn.cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.journey-condition-step-widget .btn.cancel-btn:hover {
    background: #e9e9ef;
    color: #877d88;
}

.journey-condition-step-widget .line-container {
    display: flex;
    justify-content: center;
}

.journey-condition-step-widget .line {
    width: 3px;
    height: 33px;
    background-color: #e9e9ef;
}

.journey-condition-step-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
